import { useDisclosure } from "@msuite/ui/hooks";
import { localized } from "@msuite/ui/localization";
import { Popover } from "@msuite/ui/popover";
import { createColumnHelper, createMeta, useDataTableContext } from "@msuite/ui/table";
import { YesNoTag } from "@msuite/ui/tag";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import type { ProjectMeasurement } from "@msuite/utils/types";
import RenderHooks from "render-hooks";
import { TicketTypeIcon } from "@/components/cards";
import Notes, { NoteHintButton } from "@/components/notes";
import { isClosedMenuOptions } from "./is-closed-menu-options";
import { MeasurementStatementSelect } from "./measurement-statement-select";

const columnHelper = createColumnHelper<ProjectMeasurement>();

export const columns = [
	columnHelper.accessor("date", {
		id: "date",
		header: localized("dates.date"),
		cell: (cell) => moment(cell.getValue()).dateFormat(),
	}),
	columnHelper.accessor((row) => `${row.orgName} ${row.crewRanking}`, {
		id: "crewRanking",
		header: localized("planning.crews.crew"),
		cell: (cell) => cell.getValue(),
	}),
	columnHelper.accessor((row) => `${row.ticketType} ${row.description}`, {
		id: "ticketType",
		header: localized("projects.ticket"),
		cell: (cell) => (
			<div className="h-stack">
				<TicketTypeIcon ticketTypeId={cell.row.original.ticketType} />
				<p>{cell.row.original.description}</p>
			</div>
		),
	}),
	columnHelper.accessor("statement", {
		id: "statement",
		header: localized("measurements.statement"),
		cell: (cell) => (
			<RenderHooks hooks={{ useDataTableContext }}>
				{({ useDataTableContext }) => {
					const context = useDataTableContext();
					return (
						<MeasurementStatementSelect
							row={cell.row.original}
							key={`${context.itemRenderId}-${cell.row.id}-${cell.column.id}`}
						/>
					);
				}}
			</RenderHooks>
		),
		minSize: 250,
	}),
	columnHelper.accessor("answeredBy", {
		id: "answeredBy",
		header: localized("measurements.answered_by"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor(
		(row) => {
			return row.answeredAt ? moment(row.answeredAt).longFormat() : guardString(null);
		},
		{
			id: "answeredAt",
			header: localized("measurements.answered_at"),
			cell: (cell) => cell.getValue(),
		},
	),
	columnHelper.accessor("isClosed", {
		id: "isClosed",
		header: localized("measurements.is_closed"),
		cell: (cell) => <YesNoTag value={cell.getValue()} />,
		meta: createMeta({
			menuOptions: isClosedMenuOptions(),
		}),
	}),
	columnHelper.accessor("closedBy", {
		id: "closedBy",
		header: localized("measurements.closed_by"),
		cell: (cell) => guardString(cell.getValue()),
	}),
	columnHelper.accessor("closedAt", {
		id: "closedAt",
		header: localized("measurements.closed_at"),
		cell: (cell) => (cell.getValue() ? moment(cell.getValue()).longFormat() : guardString(null)),
	}),
	columnHelper.display({
		id: "notes",
		header: localized("notes.notes"),
		cell: (cell) => (
			<RenderHooks hooks={hooks}>
				{(hooks) => {
					const disclosure = hooks.useDisclosure();

					return (
						<Popover
							trigger="click"
							variant="plain"
							placement="bottom-end"
							render={() => (
								<div className="w-[700px] max-h-full shrink">
									<Notes
										notesTable="ticketAssignmentNotes"
										identifier={cell.row.original.id}
										draggable
									/>
								</div>
							)}
						>
							<NoteHintButton
								notesTable="ticketAssignmentNotes"
								identifier={cell.row.original.id}
								disclosure={disclosure}
							/>
						</Popover>
					);
				}}
			</RenderHooks>
		),
	}),
];

const hooks = { useDataTableContext, useDisclosure };
